import React, { useState } from 'react';
import { X } from 'lucide-react';
import { IconButton, PrimaryButton, TertiaryButton } from '../../../components/brand/BrandButton';
import { Heading } from '../../../components/brand/BrandTypography';

const SectionEditorModal = ({ section, onSave, onClose, isNew = false }) => {
    const [formData, setFormData] = useState({
        section_id: section?.section_id || '',
        title: section?.title || '',
        content: section?.content || '',
        icon: section?.icon || null,
        quote: section?.quote || '',
        order: section?.order || 0,
        is_visible: section?.is_visible !== false,
    });
    const [saving, setSaving] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSave(formData, isNew);
            onClose();
        } catch (error) {
            console.error('Error saving section:', error);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white border-b border-[#FFBB98] p-4 flex items-center justify-between">
                    <Heading as="h2" size="text-lg">
                        {isNew ? 'Add Section' : 'Edit Section'}
                    </Heading>
                    <IconButton onClick={onClose}>
                        <X className="w-5 h-5" />
                    </IconButton>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {isNew && (
                        <div>
                            <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                                Section ID (snake_case)
                            </label>
                            <input
                                type="text"
                                value={formData.section_id}
                                onChange={(e) => setFormData({...formData, section_id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                                className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                                placeholder="e.g., about_us"
                                required
                            />
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                            placeholder="e.g., Why we built this"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Content (supports line breaks)
                        </label>
                        <textarea
                            value={formData.content}
                            onChange={(e) => setFormData({...formData, content: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95] min-h-[150px]"
                            placeholder="The content of this section..."
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Icon (optional)
                        </label>
                        <select
                            value={formData.icon || ''}
                            onChange={(e) => setFormData({...formData, icon: e.target.value || null})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                        >
                            <option value="">None</option>
                            <option value="Heart">Heart</option>
                            <option value="Users">Users</option>
                            <option value="Sparkles">Sparkles</option>
                            <option value="Globe">Globe</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Quote/Callout (optional)
                        </label>
                        <textarea
                            value={formData.quote || ''}
                            onChange={(e) => setFormData({...formData, quote: e.target.value})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95] min-h-[80px]"
                            placeholder="An optional quote or callout..."
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-[#1D6B6B] mb-1">
                            Display Order
                        </label>
                        <input
                            type="number"
                            min="0"
                            value={formData.order}
                            onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})}
                            className="w-full px-3 py-2 rounded-lg border border-[#FFBB98] bg-white focus:outline-none focus:ring-2 focus:ring-[#7D8E95]"
                        />
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="is_visible"
                            checked={formData.is_visible}
                            onChange={(e) => setFormData({...formData, is_visible: e.target.checked})}
                            className="w-4 h-4 rounded border-[#FFBB98]"
                        />
                        <label htmlFor="is_visible" className="text-sm text-[#1D6B6B]">
                            Visible on page
                        </label>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <TertiaryButton type="button" onClick={onClose} className="flex-1">
                            Cancel
                        </TertiaryButton>
                        <PrimaryButton type="submit" disabled={saving} className="flex-1">
                            {saving ? 'Saving...' : (isNew ? 'Add Section' : 'Save Changes')}
                        </PrimaryButton>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SectionEditorModal;
